import ActionDropdown from '@/components/basicComponents/actionDropdown';
import { EllipsisWithTooltip } from '@/components/basicComponents/ellipsisWithTooltip';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { Loader } from '@/components/loaders/loader';
import NoResults from '@/components/table/noResults';
import { TableBodyWrapper, TableRowCell, TableRowWrapper, TableWrapper, TextCell } from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { TableColumns } from '@/components/table/types';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { jsonEditorTheme, themeVar } from '@/styles/theme';
import { AIFieldType } from '@trace-8/trace8-schema';
import { JsonEditor } from 'json-edit-react';
import styled from 'styled-components';
import { IAiSuggestionsTableData } from '../types';

export const aiSuggestionsTableColumns: TableColumns[] = [
  { title: 'Strategy', key: null, width: 31, keySort: 'strategy' },
  { title: 'AI Suggestion', key: null, width: 31, keySort: 'ai suggestion' },
  { title: 'Answer', key: null, width: 31, keySort: 'answer' },
  { title: '', key: null, width: 7, keySort: 'action dropdown' }
];

export const getAiSuggestionsTableCellValue = (row: IAiSuggestionsTableData, cellKey: 'aiSuggestion' | 'answer') => {
  if (row.fieldType === getEnumKeyByValue(AIFieldType, AIFieldType.JSON)) {
    return row[cellKey] ? (
      <JsonEditor data={row[cellKey]} maxWidth="100%" viewOnly={true} theme={jsonEditorTheme} enableClipboard={false} rootName="" />
    ) : (
      <>--</>
    );
  }

  return (
    <EllipsisWithTooltip title={row[cellKey] || '--'} maxContentWidth={680} lines={2}>
      {row[cellKey] || '--'}
    </EllipsisWithTooltip>
  );
};

interface AiSuggestionsTableProp {
  tableData: IAiSuggestionsTableData[];
  loading: boolean;
  updatingRowId: string | null;
  openEditFieldPage: ({ recordId, fieldType, fieldId }: { recordId: string; fieldType: string; fieldId: string }) => void;
}

export const AiSuggestionsTable = ({ tableData, loading, updatingRowId, openEditFieldPage }: AiSuggestionsTableProp) => {
  return (
    <TableWrapper padding="0">
      <TableHeader columns={aiSuggestionsTableColumns} refetch={() => {}} />
      <TableBodyWrapper>
        {loading ? (
          <Loader />
        ) : (
          <>
            {!tableData.length ? (
              <NoResults />
            ) : (
              <>
                {tableData.map((row) => (
                  <Tooltip
                    key={row.recordId}
                    title="This row is disabled because it has no previous AI run."
                    disableTooltip={row.isPreviousAiRunExists}
                  >
                    <CustomTableRowWrapper loading={updatingRowId === row.recordId ? 1 : 0} disabled={!row.isPreviousAiRunExists}>
                      <TableRowCell width={aiSuggestionsTableColumns[0].width}>
                        <TextCell>{row.strategy}</TextCell>
                      </TableRowCell>
                      <TableRowCell width={aiSuggestionsTableColumns[1].width}>
                        <TextCell>{getAiSuggestionsTableCellValue(row, 'aiSuggestion')}</TextCell>
                      </TableRowCell>
                      <TableRowCell width={aiSuggestionsTableColumns[2].width}>
                        <TextCell>{getAiSuggestionsTableCellValue(row, 'answer')}</TextCell>
                      </TableRowCell>
                      <TableRowCell width={aiSuggestionsTableColumns[3].width}>
                        {row.isPreviousAiRunExists && updatingRowId !== row.recordId && (
                          <ActionDropdownWrapper>
                            <ActionDropdown
                              options={[
                                {
                                  name: 'Edit',
                                  onClick: () => openEditFieldPage({ recordId: row.recordId, fieldType: row.fieldType, fieldId: row.fieldId })
                                }
                              ]}
                            />
                          </ActionDropdownWrapper>
                        )}
                      </TableRowCell>
                    </CustomTableRowWrapper>
                  </Tooltip>
                ))}
              </>
            )}
          </>
        )}
      </TableBodyWrapper>
    </TableWrapper>
  );
};

const CustomTableRowWrapper = styled(TableRowWrapper)<{ disabled: boolean }>`
  opacity: ${({ disabled }) => (disabled ? 0.8 : 1)};
  background-color: ${({ disabled }) => (disabled ? themeVar('action.disabled') : themeVar('layer.1'))};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const ActionDropdownWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;
